import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "./auth";

const TABS = [
  { key: "programme_photo", label: "Programme Photos", kind: "image" },
  { key: "partner_logo", label: "Partner Logos", kind: "image" },
  { key: "document", label: "Documents", kind: "doc" },
  { key: "applications", label: "Applications", kind: "apps" },
];

const IMG_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";
const DOC_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MAX = 10 * 1024 * 1024;

function fmtSize(b) {
  if (!b) return "";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + " KB";
  return (b / 1024 / 1024).toFixed(1) + " MB";
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("programme_photo");
  const [items, setItems] = useState([]);
  const [apps, setApps] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  const activeTab = TABS.find((t) => t.key === tab);

  const loadItems = useCallback(async () => {
    if (tab === "applications") {
      const res = await fetch(`${API}/applications`, { credentials: "include" });
      if (res.ok) setApps(await res.json());
      return;
    }
    const res = await fetch(`${API}/media?category=${tab}`, { credentials: "include" });
    if (res.ok) setItems(await res.json());
  }, [tab]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const onUpload = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!file) { setMsg({ type: "err", text: "Choose a file first." }); return; }
    if (file.size > MAX) { setMsg({ type: "err", text: "File exceeds 10MB limit." }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", tab);
      fd.append("title", title);
      const res = await fetch(`${API}/media/upload`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Upload failed");
      }
      setTitle(""); setFile(null);
      const input = document.getElementById("admin-file"); if (input) input.value = "";
      setMsg({ type: "ok", text: "Uploaded." });
      loadItems();
    } catch (err) {
      setMsg({ type: "err", text: err.message });
    } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    const res = await fetch(`${API}/media/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) loadItems();
  };

  if (loading) {
    return <div style={{ minHeight: "60vh", display: "grid", placeItems: "center",
      fontFamily: "var(--mono)", color: "var(--grey)" }}>Loading&hellip;</div>;
  }
  if (!user || !user.is_admin) return null;

  return (
    <div style={{ minHeight: "100vh", background: "var(--light)" }}>
      {/* top bar */}
      <div style={{ background: "var(--deep)", color: "#fff" }}>
        <div className="shell" style={{ display: "flex", alignItems: "center", height: 68, gap: 16 }}>
          <span style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "1.2rem" }}>
            Social <span style={{ color: "var(--accent-lift)" }}>Fellows</span>
          </span>
          <span style={{ fontFamily: "var(--mono)", fontSize: ".62rem", letterSpacing: ".2em",
            textTransform: "uppercase", color: "rgba(255,255,255,.5)" }}>Admin</span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: ".82rem", color: "rgba(255,255,255,.7)" }}>{user.email}</span>
            <button className="btn btn-light" style={{ padding: "10px 18px" }}
              onClick={async () => { await logout(); navigate("/login"); }}>Sign out</button>
          </div>
        </div>
      </div>

      <div className="shell" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <p className="eyebrow">Media &amp; files</p>
        <h1 style={{ fontFamily: "var(--display)", color: "var(--navy)", fontSize: "2rem",
          margin: "0 0 24px", letterSpacing: "-.02em" }}>Upload &amp; manage</h1>

        {/* tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 30 }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); setMsg(null); }}
              className="btn" style={{
                background: tab === t.key ? "var(--accent)" : "#fff",
                color: tab === t.key ? "#fff" : "var(--navy)",
                border: tab === t.key ? "1px solid var(--accent)" : "1px solid var(--rule)",
              }}>{t.label}</button>
          ))}
        </div>

        {/* upload panel (not for applications) */}
        {tab !== "applications" && (
          <form onSubmit={onUpload} className="card" style={{ padding: 28, marginBottom: 34 }}>
            <div className="form-grid">
              <div className="frow">
                <div>
                  <label htmlFor="admin-file">File ({activeTab.kind === "doc" ? "PDF / DOC / DOCX" : "JPG / PNG / WEBP / GIF"}, max 10MB)</label>
                  <input id="admin-file" type="file"
                    accept={activeTab.kind === "doc" ? DOC_ACCEPT : IMG_ACCEPT}
                    onChange={(e) => setFile(e.target.files[0])} />
                </div>
                <div>
                  <label htmlFor="admin-title">Title / label</label>
                  <input id="admin-title" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Bihar health camp, Aug 2026" />
                </div>
              </div>
              <div>
                <button className="btn btn-primary" type="submit" disabled={busy}>
                  {busy ? "Uploading…" : "Upload"} <span className="arw">&rarr;</span>
                </button>
                {msg && (
                  <span style={{ marginLeft: 16, fontFamily: "var(--mono)", fontSize: ".74rem",
                    color: msg.type === "ok" ? "var(--accent)" : "#c0392b" }}>{msg.text}</span>
                )}
              </div>
            </div>
          </form>
        )}

        {/* listings */}
        {tab === "applications" ? (
          <ApplicationsTable apps={apps} />
        ) : activeTab.kind === "image" ? (
          <div className="g4">
            {items.length === 0 && <Empty />}
            {items.map((m) => (
              <div key={m.id} className="card" style={{ padding: 14 }}>
                <div style={{ aspectRatio: "4/3", borderRadius: 10, overflow: "hidden",
                  background: "var(--light)", marginBottom: 12, display: "grid", placeItems: "center" }}>
                  <img src={`${API.replace("/api", "")}${m.url}`} alt={m.title}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <div style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: ".95rem",
                  color: "var(--navy)", marginBottom: 4 }}>{m.title}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: ".64rem", color: "var(--grey)" }}>{fmtSize(m.size)}</span>
                  <button onClick={() => onDelete(m.id)} style={{ background: "none", border: "none",
                    color: "#c0392b", fontFamily: "var(--mono)", fontSize: ".66rem", letterSpacing: ".1em",
                    textTransform: "uppercase", cursor: "pointer" }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="routes" style={{ borderTop: "1px solid var(--rule)" }}>
            {items.length === 0 && <Empty />}
            {items.map((m) => (
              <div key={m.id} className="route" style={{ display: "flex", alignItems: "center",
                justifyContent: "space-between", gap: 16 }}>
                <div>
                  <b>{m.title}</b>
                  <span style={{ display: "block" }}>{m.content_type} · {fmtSize(m.size)}</span>
                </div>
                <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
                  <a href={`${API.replace("/api", "")}${m.url}`} target="_blank" rel="noopener noreferrer"
                    style={{ fontFamily: "var(--mono)", fontSize: ".66rem", letterSpacing: ".1em",
                    textTransform: "uppercase" }}>View</a>
                  <button onClick={() => onDelete(m.id)} style={{ background: "none", border: "none",
                    color: "#c0392b", fontFamily: "var(--mono)", fontSize: ".66rem", letterSpacing: ".1em",
                    textTransform: "uppercase", cursor: "pointer" }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Empty() {
  return <p style={{ color: "var(--grey)", fontFamily: "var(--mono)", fontSize: ".8rem",
    letterSpacing: ".06em", padding: "20px 0" }}>Nothing uploaded yet.</p>;
}

function ApplicationsTable({ apps }) {
  if (!apps.length) return <Empty />;
  return (
    <table className="tbl">
      <thead>
        <tr><th>Name</th><th>Field</th><th>City</th><th>Posting</th><th>CV</th><th>Received</th></tr>
      </thead>
      <tbody>
        {apps.map((a) => (
          <tr key={a.id}>
            <td>{a.name}<br /><span style={{ color: "var(--grey)", fontSize: ".85rem" }}>{a.message}</span></td>
            <td>{a.field}</td>
            <td>{a.city}</td>
            <td>{a.posting}</td>
            <td>{a.cv_url
              ? <a href={`${API.replace("/api", "")}${a.cv_url}`} target="_blank" rel="noopener noreferrer">{a.cv_filename || "Download"}</a>
              : <span style={{ color: "var(--grey)" }}>—</span>}</td>
            <td className="n">{new Date(a.created_at).toLocaleDateString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
